import { Plus, Trash2 } from "lucide-react";

import { colors, TextField } from "@material-ui/core";

import { Button } from "@/design-system/components/Button/Button";

import { Tooltip } from "@/design-system/components/Tooltip/Tooltip";

import { ConfigureDestinationsModal } from "@/destinations/modals/ConfigureDestinationsModal";
import { KeyValueMappingControl } from "@/destinations/modals/ConfigureIntegrationModal";
import { DestinationConfigRow } from "../shared/ConfigPageRow";

const WebhooksEndpointListConfigurationControl = ({
  field,
  value = [],
  onChange,
}) => {
  const onWebhookChange = (index, newWebhook) => {
    const newValue = value.slice();
    newValue[index] = newWebhook;
    onChange(field.key, newValue);
  };

  const onDelete = (index) => {
    const newValue = value.slice();
    newValue.splice(index, 1);
    onChange(field.key, newValue);
  };

  return (
    <div className="justify-items-stretch" style={{ width: "100%" }}>
      {value.map((webhook, index) => {
        return (
          <div
            className="border-borderSubtle mb-3 rounded-md border p-3"
            key={index}
          >
            <div className="mb-1 flex items-center justify-between">
              <span className="text-textSubtle text-xs font-medium">
                Webhook {index + 1}
              </span>
              <Tooltip
                content="Delete webhook"
                contentProps={{ className: "z-[1400]" }}
              >
                <div
                  className="text-textSubtle hover:text-textDefault flex cursor-pointer items-center justify-center rounded-full p-1"
                  onClick={() => onDelete(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </div>
              </Tooltip>
            </div>
            <WebhookEndpointConfigurationControl
              field={field}
              webhook={webhook}
              onWebhookChange={(newWebhook) =>
                onWebhookChange(index, newWebhook)
              }
            />
          </div>
        );
      })}
      <Button
        variant="secondary"
        size="xs"
        onClick={() => onChange(field.key, [...value, { key: "", value: "" }])}
        trailingIcon={<Plus />}
      >
        Add Webhook
      </Button>
    </div>
  );
};

const WebhookEndpointConfigurationControl = ({
  field,
  webhook,
  onWebhookChange,
}) => {
  const handleWebhookChange = (webhookChanges) => {
    const newWebhook = { ...webhook, ...webhookChanges };
    onWebhookChange(newWebhook);
  };

  return (
    <div className="flex flex-col gap-3" style={{ width: "100%" }}>
      <TextField
        fullWidth
        size="small"
        autoComplete="off"
        label="Webhook URL"
        name="Webhook URL"
        onChange={(event) =>
          handleWebhookChange({ endpoint: event.target.value })
        }
        value={webhook.endpoint || ""}
        key={field.name}
        variant="outlined"
        type="text"
        inputProps={{ style: { fontFamily: "monospace" } }}
        className="fs-exclude"
      />
      <KeyValueMappingControl
        field={field}
        value={webhook.headers || []}
        onChange={(fieldKey, newMapping) =>
          handleWebhookChange({ headers: newMapping })
        }
        keyLabel="Header name"
        valueLabel="Header value"
        objectLabel="Header"
      />
    </div>
  );
};

const WebhooksModal = ({
  destination,
  open,
  onClose,
  handleUpdateConfigureDest,
  existingConfig,
}) => {
  const settings = existingConfig?.settings || {};

  return (
    <ConfigureDestinationsModal
      settingsToControl={{
        webhooks: WebhooksEndpointListConfigurationControl,
      }}
      limitSettings={["webhooks"]}
      open={open}
      settings={settings}
      dest={destination}
      onClose={onClose}
      onSave={(_dest, _id, newConfig) => handleUpdateConfigureDest(newConfig)}
      existingConfig={existingConfig}
    />
  );
};

const WebhooksRow = ({ setConfigModalOpen, existingConfig }) => {
  const settings = existingConfig?.settings || {};

  const webhooksSet =
    settings.webhooks &&
    settings.webhooks.length > 0 &&
    settings.webhooks[0].endpoint;

  return (
    <DestinationConfigRow
      name="Webhooks"
      textColor={webhooksSet ? "" : colors.red[600]}
      labelText={webhooksSet ? "Configured" : "Not configured"}
      onOpenConfigModal={() => setConfigModalOpen("configuration")}
    />
  );
};

const ConfigurationRow = ({ setConfigModalOpen, existingConfig }) => {
  const settings = existingConfig?.settings || {};

  const webhooksSet =
    settings.webhooks &&
    settings.webhooks.length > 0 &&
    settings.webhooks[0].endpoint;

  return (
    <DestinationConfigRow
      disabled={!webhooksSet}
      name="Options"
      labelText=""
      onOpenConfigModal={() => {
        if (webhooksSet) {
          setConfigModalOpen();
        }
      }}
    />
  );
};

const ConfigurationModal = ({
  destination,
  open,
  onClose,
  handleUpdateConfigureDest,
  existingConfig,
}) => {
  const settings = existingConfig?.settings || {};

  const limitSettings = ["enableIdentifyEvents", "enablePageviewEvents"];

  return (
    <ConfigureDestinationsModal
      limitSettings={limitSettings}
      open={open}
      settings={settings}
      dest={destination}
      onClose={onClose}
      onSave={(_dest, _id, newConfig) => handleUpdateConfigureDest(newConfig)}
      existingConfig={existingConfig}
    />
  );
};

const WEBHOOKS = "WEBHOOKS";
const OPTIONS = "OPTIONS";

const Rows = {
  WEBHOOKS: WebhooksRow,
  OPTIONS: ConfigurationRow,
};

const Modals = {
  WEBHOOKS: WebhooksModal,
  OPTIONS: ConfigurationModal,
};

const RowOrder = [WEBHOOKS, OPTIONS];

export default {
  Rows,
  RowOrder,
  Modals,
};
